// types
import {
	TraceabilityCurrency,
	TraceabilityTransactions,
	TraceabilityType,
} from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid } from '@mui/material';

// ui
import { Flag } from '../../../ui/Flag/Flag';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// utils
import { flagsByCurrency, formatCurrency } from '../../../../utils/currency';
import { formatNumber, formatPercentage } from '../../../../utils/format';

export interface TableBasicTraceabilityCurrenciesProps extends Omit<TableProps, 'headers'> {
	data: TraceabilityCurrency[];
	traceability: TraceabilityTransactions;
	type: TraceabilityType;
}

export const TableBasicTraceabilityCurrencies = ({
	data = [],
	traceability,
	type = 'TRANSACTIONS',
	...props
}: TableBasicTraceabilityCurrenciesProps) => {
	// hooks
	const { t } = useTranslation();

	const headers: TableHeader[] = [
		{
			label: t('currency'),
			render: (currency: string) => {
				return (
					<Grid alignItems="center" container spacing={1} wrap="nowrap">
						<Grid item>
							<Flag code={flagsByCurrency[currency]} />
						</Grid>
						<Grid item>{currency}</Grid>
					</Grid>
				);
			},
			value: 'currency',
		},
	];

	if (type === 'FINANCIAL') {
		headers.push(
			{
				label: t('partiallyEvidenced'),
				render: (
					partial: TraceabilityCurrency['partial'],
					header: TableHeader,
					item: TraceabilityCurrency
				) => {
					return typeof partial === 'number'
						? formatCurrency({ currency: item.currency, notation: 'compact', value: partial })
						: t('na');
				},
				value: 'partial',
			},
			{
				label: t('notEvidenced'),
				render: (
					undocumented: TraceabilityCurrency['undocumented'],
					header: TableHeader,
					item: TraceabilityCurrency
				) => {
					return typeof undocumented === 'number'
						? formatCurrency({ currency: item.currency, notation: 'compact', value: undocumented })
						: t('na');
				},
				value: 'undocumented',
			},
			{
				label: t('evidenced'),
				render: (
					documented: TraceabilityCurrency['documented'],
					header: TableHeader,
					item: TraceabilityCurrency
				) => {
					return typeof documented === 'number'
						? formatCurrency({ currency: item.currency, notation: 'compact', value: documented })
						: t('na');
				},
				value: 'documented',
			},
			{
				label: t('total'),
				render: (
					total: TraceabilityCurrency['total'],
					header: TableHeader,
					item: TraceabilityCurrency
				) => {
					return typeof total === 'number'
						? formatCurrency({ currency: item.currency, notation: 'compact', value: total })
						: t('na');
				},
				value: 'total',
			}
		);
	} else if (type === 'TRANSACTIONS') {
		headers.push(
			{
				label: t('partiallyEvidenced'),
				render: (partial: TraceabilityCurrency['partial']) => {
					return typeof partial === 'number' ? formatNumber(partial) : t('na');
				},
				value: 'partial',
			},
			{
				label: t('notEvidenced'),
				render: (undocumented: number) => {
					return typeof undocumented === 'number' ? formatNumber(undocumented) : t('na');
				},
				value: 'undocumented',
			},
			{
				label: t('evidenced'),
				render: (documented: TraceabilityCurrency['documented']) => {
					return typeof documented === 'number' ? formatNumber(documented) : t('na');
				},
				value: 'documented',
			}
		);
	}

	headers.push({
		label: t('percent'),
		render: (undocumented: number) => {
			return typeof undocumented === 'number' ? formatPercentage(undocumented) : t('na');
		},
		value: 'value',
	});

	if (type === 'TRANSACTIONS') {
		headers.push({
			label: t('total'),
			render: (documented: TraceabilityCurrency['documented']) => {
				return typeof documented === 'number' ? formatNumber(documented) : t('na');
			},
			value: 'total',
		});
	}

	return (
		<Table
			borderRadius={2}
			color="grey"
			data={data}
			hasBorder={true}
			headers={headers}
			{...props}
		/>
	);
};
